import { useState } from 'react'
import { Database, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { useRemoveKnowledgeBase, AssistantKB } from '@/hooks/useAssistants'
import { AddKnowledgeBasesModal } from '@/components/modals/AddKnowledgeBasesModal'

interface KnowledgeBasesTabProps {
  assistantId: string
  knowledgeBases: AssistantKB[]
}

export function KnowledgeBasesTab({ assistantId, knowledgeBases }: KnowledgeBasesTabProps) {
  const { toast } = useToast()
  const [showAddModal, setShowAddModal] = useState(false)
  const [kbToRemove, setKbToRemove] = useState<AssistantKB | null>(null)
  
  const removeKB = useRemoveKnowledgeBase()

  const handleRemoveKB = async () => {
    if (!kbToRemove) return

    try {
      await removeKB.mutateAsync({ assistantId, kbId: kbToRemove.id })
      toast({
        title: 'Knowledge base removed',
        description: `"${kbToRemove.name}" has been removed from this assistant.`,
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to remove knowledge base',
        variant: 'destructive',
      })
    } finally {
      setKbToRemove(null)
    }
  }

  // Empty state
  if (knowledgeBases.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <Database className="h-16 w-16 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No knowledge bases connected</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Connect knowledge bases to make this assistant active
          </p>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Knowledge Base
          </Button>
        </CardContent>

        <AddKnowledgeBasesModal
          open={showAddModal}
          onOpenChange={setShowAddModal}
          assistantId={assistantId}
          existingKBIds={[]}
        />
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Connected Knowledge Bases</CardTitle>
        <Button onClick={() => setShowAddModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Knowledge Base
        </Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Documents</TableHead>
              <TableHead>Last Updated</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {knowledgeBases.map((kb) => (
              <TableRow key={kb.id}>
                <TableCell className="font-medium">{kb.name}</TableCell>
                <TableCell>{kb.document_count ?? 0} docs</TableCell>
                <TableCell className="text-muted-foreground">
                  {/* Note: We don't have updated_at in the KB data, showing placeholder */}
                  —
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => setKbToRemove(kb)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      {/* Add KB Modal */}
      <AddKnowledgeBasesModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        assistantId={assistantId}
        existingKBIds={knowledgeBases.map((kb) => kb.id)}
      />

      {/* Remove Confirmation Dialog */}
      <AlertDialog open={!!kbToRemove} onOpenChange={() => setKbToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove knowledge base?</AlertDialogTitle>
            <AlertDialogDescription>
              Remove "{kbToRemove?.name}" from this assistant? The knowledge base itself won't be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveKB}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
